import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bug, Zap, CheckCircle, AlertTriangle, Shield, Search } from 'lucide-react';
import { PerfectionistIcon } from './CustomIcons';
import { Button } from './ui/button';
import { InteractiveCard } from './InteractiveCard';
import Footer from './Footer';

export default function QATestingPage() {
  useEffect(() => {
    document.title = 'QA & Testing Services - The Perfectionist | Semando';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Comprehensive QA and automated testing by The Perfectionist. Flawless software delivery with zero-defect standards. $450/day for mission-critical quality assurance.');
    }
  }, []);
  const offerings = [
    {
      icon: Bug,
      title: 'Automated Testing',
      description: 'Comprehensive test automation frameworks that scale with your development lifecycle.'
    },
    {
      icon: Zap,
      title: 'Performance Testing',
      description: 'Load testing, stress testing, and performance optimization to ensure peak system performance.'
    },
    {
      icon: Shield,
      title: 'Security Testing',
      description: 'Penetration testing and vulnerability assessments integrated into your QA process.'
    },
    {
      icon: CheckCircle,
      title: 'Continuous Integration',
      description: 'CI/CD pipeline integration with automated testing at every stage of deployment.'
    }
  ];

  const benefits = [
    {
      icon: AlertTriangle,
      title: 'Zero-Defect Delivery',
      description: 'Identify and eliminate issues before they reach production, ensuring flawless user experiences.'
    },
    {
      icon: Zap,
      title: 'Accelerated Excellence',
      description: 'Streamlined testing processes that deliver perfect results faster than traditional methods.'
    }
  ];

  return (
    <div className="min-h-screen space-y-0">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-background to-muted/20 relative">
        <div className="absolute inset-0 cyber-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full border border-primary/20">
                <PerfectionistIcon className="h-5 w-5 text-primary mr-2" size={20} />
                <span className="text-sm text-primary">PRECISION_MODE</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight">
                <span className="block text-muted-foreground text-xl md:text-2xl mb-4">THE PERFECTIONIST</span>
                Flawless isn’t an option. <br /> It’s the only acceptable state.
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                In a world where a single flaw can cost millions, we pursue absolute perfection. Every line of code, every user interaction, every system response—tested, verified, and refined until it's flawless.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            <div className="text-center space-y-4">
              <h2 className="text-3xl md:text-4xl">Pursuit of Perfection</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Meticulous testing methodologies that leave nothing to chance, ensuring flawless delivery every time.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {offerings.map((offering, index) => (
                <InteractiveCard key={index} className="hover-neon">
                  <div className="p-8 space-y-4">
                    <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg">
                      <offering.icon className="h-8 w-8 text-primary" size={32} />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl">{offering.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{offering.description}</p>
                    </div>
                  </div>
                </InteractiveCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 bg-gradient-to-r from-muted/10 via-transparent to-muted/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl">Why Perfection Matters</h2>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  In an unforgiving digital landscape, "good enough" is never good enough. Excellence isn't optional—it's essential.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-lg text-primary">Uncompromising Standards</h3>
                  <p className="text-muted-foreground">
                    Every detail scrutinized, every edge case considered. We test beyond requirements to ensure absolute reliability.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg text-primary">Reputation Protection</h3>
                  <p className="text-muted-foreground">
                    A single software failure can destroy years of reputation building. Our perfectionist approach safeguards your brand.
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg text-primary">Zero-Tolerance Quality</h3>
                  <p className="text-muted-foreground">
                    We don't accept "minor" bugs or "acceptable" performance issues. Every imperfection is an opportunity for improvement.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid gap-6">
              {benefits.map((benefit, index) => (
                <InteractiveCard key={index}>
                  <div className="p-6 space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                        <benefit.icon className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="text-lg">{benefit.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </div>
                </InteractiveCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Helps */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-16">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl">The Perfectionist's Standard</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                When perfection is the only acceptable outcome, these are the standards we deliver without compromise.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="space-y-4 p-6 rounded-lg border border-primary/20 bg-primary/5">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto">
                  <Search className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg">Microscopic Analysis</h3>
                <p className="text-muted-foreground">Every function, every interaction analyzed with surgical precision to eliminate all imperfections.</p>
              </div>
              <div className="space-y-4 p-6 rounded-lg border border-primary/20 bg-primary/5">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg">Flawless Velocity</h3>
                <p className="text-muted-foreground">Streamlined perfection processes that deliver excellence faster than traditional methods.</p>
              </div>
              <div className="space-y-4 p-6 rounded-lg border border-primary/20 bg-primary/5">
                <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-lg mx-auto">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-lg">Unbreakable Reliability</h3>
                <p className="text-muted-foreground">Systems tested beyond their limits to ensure they never fail when it matters most.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing & CTA */}
      <section className="py-24 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent relative">
        <div className="absolute inset-0 cyber-grid opacity-20" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8 relative z-10">
          <div className="space-y-4">
            <h2 className="text-3xl md:text-4xl">Work with The Perfectionist</h2>
            <p className="text-xl text-muted-foreground">
              Daily rate for senior QA and testing perfectionist.
            </p>
            <div className="text-6xl text-primary">$450<span className="text-2xl text-muted-foreground">/day</span></div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Excellence isn't accidental—it's the result of meticulous attention to every detail, every time.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="group bg-primary text-primary-foreground hover:bg-primary/90 cyber-glow">
              <Link to="/contact">
                Demand Perfection
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}